import React, { useState, Children } from 'react';
import { ArrowLeft, BookOpen, Camera, Pen, Sparkles as SparklesIcon, Rainbow, Pencil, ChevronDown } from 'lucide-react';
type BookStyle = 'standard' | 'photo' | 'classic' | 'modern' | 'childrens';
type PageCount = 8 | 16 | 24 | 32;
type ReadingLevel = 'toddler' | 'adolescent' | 'preteen' | 'teen';
type ArtStyle = 'anime' | 'watercolor' | 'ghibli' | 'oil' | 'digital' | 'realistic';
type StorybookSetupPageProps = {
  storyId: number;
  onBack: () => void;
  onContinue: (config: BookConfiguration) => void;
};
export type BookConfiguration = {
  bookStyle: BookStyle;
  pageCount: PageCount;
  readingLevel: ReadingLevel;
  artStyle: ArtStyle;
};
export function StorybookSetupPage({
  storyId,
  onBack,
  onContinue
}: StorybookSetupPageProps) {
  const [bookStyle, setBookStyle] = useState<BookStyle>('standard');
  const [pageCount, setPageCount] = useState<PageCount>(8);
  const [readingLevel, setReadingLevel] = useState<ReadingLevel>('adolescent');
  const [artStyle, setArtStyle] = useState<ArtStyle>('anime');
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  // Mock story data
  const story = {
    id: storyId,
    thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=400&fit=crop',
    title: 'greek mythos',
    date: 'December 2, 2025',
    duration: '3:47'
  };
  const handleContinue = () => {
    onContinue({
      bookStyle,
      pageCount,
      readingLevel,
      artStyle
    });
  };
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-2337">
      <div className="px-8 py-6" data-id="element-2338">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6" data-id="element-2339">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium" data-id="element-2340">
            <ArrowLeft className="w-5 h-5" data-id="element-2341" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-2342">Storybook Setup</h1>
        </div>

        <div className="flex gap-6" data-id="element-2343">
          {/* Main Content */}
          <div className="flex-1 space-y-6" data-id="element-2344">
            {/* Story Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2345">
              <div className="flex items-center justify-between" data-id="element-2346">
                <div className="flex items-center gap-4" data-id="element-2347">
                  <img src={story.thumbnail} alt={story.title} className="w-20 h-20 rounded-xl object-cover" data-id="element-2348" />
                  <div data-id="element-2349">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1" data-id="element-2350">
                      {story.title}
                    </h2>
                    <p className="text-gray-600" data-id="element-2351">
                      {story.date} • {story.duration}
                    </p>
                  </div>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-2352">
                  <Pencil className="w-4 h-4" data-id="element-2353" />
                  Edit
                </button>
              </div>
            </div>

            {/* Book Style */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2354">
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2355">
                Book Style
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2356">
                Choose the visual aesthetic for your book
              </p>

              <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-2357">
                <button onClick={() => setBookStyle('standard')} className={`p-6 rounded-2xl border-2 transition-all ${bookStyle === 'standard' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2358">
                  <BookOpen className="w-8 h-8 mx-auto mb-3 text-gray-700" data-id="element-2359" />
                  <div className="font-semibold text-gray-900 mb-1" data-id="element-2360">
                    Standard
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2361">
                    Text on one page, image on the other
                  </div>
                </button>

                <button onClick={() => setBookStyle('photo')} className={`p-6 rounded-2xl border-2 transition-all ${bookStyle === 'photo' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2362">
                  <Camera className="w-8 h-8 mx-auto mb-3 text-gray-700" data-id="element-2363" />
                  <div className="font-semibold text-gray-900 mb-1" data-id="element-2364">
                    Photo Book
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2365">
                    Many images, limited text
                  </div>
                </button>

                <button onClick={() => setBookStyle('classic')} className={`p-6 rounded-2xl border-2 transition-all ${bookStyle === 'classic' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2366">
                  <Pen className="w-8 h-8 mx-auto mb-3 text-gray-700" data-id="element-2367" />
                  <div className="font-semibold text-gray-900 mb-1" data-id="element-2368">
                    Classic
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2369">
                    Mostly text, fewer images
                  </div>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-2370">
                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2371">
                  <SparklesIcon className="w-8 h-8 mx-auto mb-3 text-gray-400" data-id="element-2372" />
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2373">Modern</div>
                  <div className="text-sm text-gray-400" data-id="element-2374">
                    Contemporary layout
                  </div>
                </button>

                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2375">
                  <Rainbow className="w-8 h-8 mx-auto mb-3 text-gray-400" data-id="element-2376" />
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2377">
                    Children's
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-2378">
                    Colorful & playful
                  </div>
                </button>
              </div>
            </div>

            {/* Book Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2379">
              <h3 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-2380">
                Book Settings
              </h3>

              {/* Page Count */}
              <div className="mb-8" data-id="element-2381">
                <h4 className="font-semibold text-gray-900 mb-4" data-id="element-2382">Page Count</h4>
                <div className="space-y-3" data-id="element-2383">
                  {[8, 16, 24, 32].map(count => <button key={count} onClick={() => count === 8 && setPageCount(count as PageCount)} disabled={count !== 8} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${pageCount === count ? 'border-green-500 bg-green-50' : count === 8 ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 opacity-50 cursor-not-allowed'}`} data-id="element-2384">
                      <div className="flex items-center gap-3" data-id="element-2385">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${pageCount === count ? 'border-green-500' : 'border-gray-300'}`} data-id="element-2386">
                          {pageCount === count && <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2387"></div>}
                        </div>
                        <span className="font-medium text-gray-900" data-id="element-2388">
                          {count} pages
                        </span>
                      </div>
                      <span className={`text-sm ${count === 8 ? 'text-gray-900 font-medium' : 'text-gray-400'}`} data-id="element-2389">
                        {count === 8 ? '5 credits' : 'Coming Soon'}
                      </span>
                    </button>)}
                </div>
              </div>

              {/* Reading Level */}
              <div data-id="element-2390">
                <h4 className="font-semibold text-gray-900 mb-4" data-id="element-2391">
                  Reading Level
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-2392">
                  <button onClick={() => setReadingLevel('toddler')} className={`p-6 rounded-2xl border-2 transition-all ${readingLevel === 'toddler' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2393">
                    <div className="text-4xl mb-2" data-id="element-2394">👶</div>
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2395">
                      Toddler
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-2396">
                      Ages 1-3, simple words
                    </div>
                  </button>

                  <button onClick={() => setReadingLevel('adolescent')} className={`p-6 rounded-2xl border-2 transition-all ${readingLevel === 'adolescent' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2397">
                    <div className="text-4xl mb-2" data-id="element-2398">😊</div>
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2399">
                      Adolescent
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-2400">
                      Ages 4-7, easy sentences
                    </div>
                  </button>

                  <button onClick={() => setReadingLevel('preteen')} className={`p-6 rounded-2xl border-2 transition-all ${readingLevel === 'preteen' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2401">
                    <div className="text-4xl mb-2" data-id="element-2402">😀</div>
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2403">
                      Pre-teen
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-2404">
                      Ages 8-12, chapter books
                    </div>
                  </button>

                  <button onClick={() => setReadingLevel('teen')} className={`p-6 rounded-2xl border-2 transition-all ${readingLevel === 'teen' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2405">
                    <div className="text-4xl mb-2" data-id="element-2406">😺</div>
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-2407">Teen</div>
                    <div className="text-sm text-gray-600" data-id="element-2408">
                      Ages 13-17, young adult
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Art Style */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2409">
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2410">
                Art Style
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-2411">
                Choose the illustration style for your book
              </p>

              <div className="grid grid-cols-3 gap-4" data-id="element-2412">
                <button onClick={() => setArtStyle('anime')} className={`p-6 rounded-2xl border-2 transition-all ${artStyle === 'anime' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2413">
                  <div className="text-4xl mb-2" data-id="element-2414">🌸</div>
                  <div className="font-semibold text-gray-900" data-id="element-2415">Anime</div>
                </button>

                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2416">
                  <div className="text-4xl mb-2" data-id="element-2417">🎨</div>
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2418">
                    Watercolor
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-2419">Coming Soon</div>
                </button>

                <button onClick={() => setArtStyle('ghibli')} className={`p-6 rounded-2xl border-2 transition-all ${artStyle === 'ghibli' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2420">
                  <div className="text-4xl mb-2" data-id="element-2421">🌿</div>
                  <div className="font-semibold text-gray-900" data-id="element-2422">
                    Studio Ghibli
                  </div>
                </button>

                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2423">
                  <div className="text-4xl mb-2" data-id="element-2424">🖼️</div>
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2425">
                    Oil Painting
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-2426">Coming Soon</div>
                </button>

                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2427">
                  <div className="text-4xl mb-2" data-id="element-2428">💻</div>
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2429">
                    Digital Art
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-2430">Coming Soon</div>
                </button>

                <button disabled className="p-6 rounded-2xl border-2 border-gray-200 opacity-50 cursor-not-allowed" data-id="element-2431">
                  <div className="text-4xl mb-2" data-id="element-2432">📷</div>
                  <div className="font-semibold text-gray-500 mb-1" data-id="element-2433">
                    Realistic
                  </div>
                  <div className="text-xs text-gray-400" data-id="element-2434">Coming Soon</div>
                </button>
              </div>
            </div>

            {/* Advanced Options */}
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2435">
              <button onClick={() => setAdvancedExpanded(!advancedExpanded)} className="w-full flex items-center justify-between" data-id="element-2436">
                <div data-id="element-2437">
                  <h3 className="text-xl font-semibold text-gray-900 text-left" data-id="element-2438">
                    Advanced Options
                  </h3>
                  <p className="text-gray-600 text-left" data-id="element-2439">
                    Fine-tune generation settings
                  </p>
                </div>
                <ChevronDown className={`w-6 h-6 text-gray-600 transition-transform ${advancedExpanded ? 'rotate-180' : ''}`} data-id="element-2440" />
              </button>
              {advancedExpanded && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-2441">
                  <p className="text-gray-600" data-id="element-2442">
                    Advanced settings coming soon...
                  </p>
                </div>}
            </div>
          </div>

          {/* Cost Summary Sidebar */}
          <div className="w-80" data-id="element-2443">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6" data-id="element-2444">
              <div className="flex items-center gap-2 mb-6" data-id="element-2445">
                <SparklesIcon className="w-5 h-5 text-gray-700" data-id="element-2446" />
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-2447">
                  Cost Summary
                </h3>
              </div>

              <div className="space-y-3 mb-6" data-id="element-2448">
                <div className="flex justify-between text-gray-700" data-id="element-2449">
                  <span data-id="element-2450">Pages ({pageCount})</span>
                  <span className="font-medium" data-id="element-2451">5 credits</span>
                </div>
                <div className="flex justify-between text-gray-700" data-id="element-2452">
                  <span data-id="element-2453">Model</span>
                  <span className="font-medium" data-id="element-2454">1x</span>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 mb-6" data-id="element-2455">
                <div className="flex justify-between items-center" data-id="element-2456">
                  <span className="font-semibold text-gray-900" data-id="element-2457">
                    Total Cost
                  </span>
                  <span className="text-2xl font-bold text-gray-900" data-id="element-2458">
                    5 credits
                  </span>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-4 mb-6" data-id="element-2459">
                <div className="flex items-center justify-between mb-2" data-id="element-2460">
                  <span className="text-sm text-gray-600" data-id="element-2461">Available</span>
                  <SparklesIcon className="w-4 h-4 text-green-600" data-id="element-2462" />
                </div>
                <div className="text-3xl font-bold text-green-600 mb-1" data-id="element-2463">35</div>
                <div className="text-sm text-gray-600" data-id="element-2464">of 50 credits</div>
              </div>

              <button onClick={handleContinue} className="w-full py-3 bg-[#4ade80] text-white font-semibold rounded-xl hover:bg-[#22c55e] transition-colors" data-id="element-2465">
                Continue to Review
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}